import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, query, where, addDoc, updateDoc, Timestamp, getDocs, arrayUnion } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { Offer, Listing, User, FileData } from '../types';
import { fetchOfferDocuments } from '../services/offerService';
import { calculatePtcScore, buildPtcInputFromOffer } from '../services/ptcCalculator';
import OfferEditModal from './OfferEditModal';
import './OfferDetail.css';

interface AgentNote {
  id?: string;
  offerId?: string; // Deprecated: no longer used for lookup; kept for backward compat
  createdByUserId: string; // Listing agent who created the note
  buyerAgentEmail: string; // Buyer's agent email
  buyerAgentName: string; // Buyer's agent name
  buyerAgentUserId?: string; // Buyer's agent userId if registered
  listingAgentEmail?: string; // Email of listing agent (note creator)
  listingAgentName?: string; // Name of listing agent (note creator)
  rating: number;
  note: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

const OfferDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [listing, setListing] = useState<Listing | null>((location.state as any)?.listing ?? null);
  const [loading, setLoading] = useState(true);
  const [showRecommendations, setShowRecommendations] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [buyerAgentProfile, setBuyerAgentProfile] = useState<User | null>(null);
  const [agentNote, setAgentNote] = useState<AgentNote | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [savingNote, setSavingNote] = useState(false);
  const [files, setFiles] = useState<FileData[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [purchaseAgreementFiles, setPurchaseAgreementFiles] = useState<FileData[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploadingPreApproval, setUploadingPreApproval] = useState(false);
  const [preApprovalFile, setPreApprovalFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [lenderExperience, setLenderExperience] = useState<number | null>(null);
  const [lenderExperienceLoading, setLenderExperienceLoading] = useState(false);
  // True once lender experience is resolved (from cache or fresh fetch or no NMLS to look up).
  // Auto-save is blocked until this is true to prevent saving a score computed with the wrong default.
  const [lenderExperienceReady, setLenderExperienceReady] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
 
  const fetchBuyerAgentProfile = async (userId: string) => {
    try {
      const agentDoc = await getDoc(doc(db, 'userProfiles', userId));
      if (agentDoc.exists()) {
        const agentData = { uid: agentDoc.id, ...agentDoc.data() } as User;
        setBuyerAgentProfile(agentData);
      }
    } catch (error) {
      console.error('Error fetching buyer agent profile:', error);
    }
  };

  const fetchAgentNote = async (agentEmail: string) => {
    if (!currentUser) return;

    try {
      const notesQuery = query(
        collection(db, 'agentNotes'),
        where('createdByUserId', '==', currentUser.uid),
        where('buyerAgentEmail', '==', agentEmail)
      );

      const notesSnapshot = await getDocs(notesQuery);

      if (!notesSnapshot.empty) {
        const noteDoc = notesSnapshot.docs[0];
        const noteData = { id: noteDoc.id, ...noteDoc.data() } as AgentNote;
        setAgentNote(noteData);
        setNoteText(noteData.note);
        setRating(noteData.rating);
      } else {
        setAgentNote(null);
        setNoteText('');
        setRating(0);
      }
    } catch (error) {
      console.error('Error fetching agent note:', error);
    }
  };

  useEffect(() => {
    const fetchOffer = async () => {
      if (!id) {
        return;
      }

      try {
        setLoading(true);
        const offerDoc = await getDoc(doc(db, 'offers', id));

        if (offerDoc.exists()) {
          const offerData = {
            id: offerDoc.id,
            ...offerDoc.data(),
            submissionDate: offerDoc.data().submissionDate?.toDate()
          } as Offer;
          setOffer(offerData);

          // Seed lender experience from cached value on the offer document so the
          // first liveScore computation uses the right value instead of the default.
          if (typeof offerData.lenderExperienceYears === 'number') {
            setLenderExperience(offerData.lenderExperienceYears);
            setLenderExperienceReady(true);
          }

          // Fetch buyer agent profile using userId from offer
          if (offerData.userId) {
            fetchBuyerAgentProfile(offerData.userId);
          }

          // Fetch listing if exists
          if (offerData.listingId) {
            const listingDoc = await getDoc(doc(db, 'listings', offerData.listingId));
            if (listingDoc.exists()) {
              const listingData = { id: listingDoc.id, ...listingDoc.data() } as Listing;
              setListing(listingData);
            }
          }

          // Fetch offer documents (files and purchase agreement files)
          setLoadingDocuments(true);
          const offerDocuments = await fetchOfferDocuments(id);
          // Raw files from the offer document always carry downloadURL (written by uploadFile)
          const offerDocFiles: FileData[] = offerData.files || [];

          // Helper: ensure every file has a valid downloadURL.
          // offer-documents items may use 'storagePath' instead of 'filePath', or
          // may be missing downloadURL entirely — resolve them from Firebase Storage.
          const resolveDownloadURLs = async (files: FileData[]): Promise<FileData[]> => {
            return Promise.all(files.map(async (file) => {
              if (file.downloadURL && file.downloadURL.startsWith('https://')) return file;
              // Try matching by fileName from the offer's own files array (always has downloadURL)
              const match = offerDocFiles.find(f => f.fileName === file.fileName && f.downloadURL?.startsWith('https://'));
              if (match) return { ...file, downloadURL: match.downloadURL };
              // Last resort: fetch fresh URL from Firebase Storage
              const path = file.filePath || (file as any).storagePath;
              if (!path) return file;
              try {
                const url = await getDownloadURL(ref(storage, path));
                return { ...file, downloadURL: url };
              } catch { return file; }
            }));
          };

          if (offerDocuments) {
            const [resolvedFiles, resolvedPaFiles] = await Promise.all([
              resolveDownloadURLs(offerDocuments.files || []),
              resolveDownloadURLs(offerDocuments.purchaseAgreementFiles || []),
            ]);
            setFiles(resolvedFiles);
            setPurchaseAgreementFiles(resolvedPaFiles);
          } else {
            // Fallback to legacy data structure if offer-documents doesn't exist
            setFiles(offerDocFiles);
            setPurchaseAgreementFiles(offerData.purchaseAgreementFiles || []);
          }
          setLoadingDocuments(false);

          // Fetch agent note for selling agent if available
          // v2 schema first, then v1 fallback
          const sellingAgentEmail = offerData.extractedData?.agents?.buyer_broker?.email
            || offerData.combinedOfferData?.purchaseAgreement?.buyerAgent?.agentEmail;
          if (sellingAgentEmail) {
            fetchAgentNote(sellingAgentEmail);
          }
        }
      } catch (error) {
        console.error('OfferDetail: Error fetching offer:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOffer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch lender experience when offer is loaded
  // v2 schema: extractedData.pre_approval.loan_officer_nmls; v1 fallback: combinedOfferData.preApproval.lender.nmlsId
  const lenderNmlsId = offer?.extractedData?.pre_approval?.loan_officer_nmls
    || offer?.combinedOfferData?.preApproval?.lender?.nmlsId;

  useEffect(() => {
    if (lenderNmlsId) {
      fetchLenderExperience(lenderNmlsId);
    } else {
      // No NMLS on this offer — nothing to fetch, score can be saved immediately.
      setLenderExperienceReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lenderNmlsId]);

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return 'N/A';
    try {
      const d = typeof date === 'string' ? new Date(date) : date;
      if (!(d instanceof Date) || isNaN(d.getTime())) return 'N/A';
      return d.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', date, error);
      return 'N/A';
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };



  const handleSaveNote = async () => {
    if (!currentUser || !offer || !offer.id) return;
    
    // v2 schema first, then v1 fallback
    const agentEmailV2 = offer.extractedData?.agents?.buyer_broker?.email;
    const agentEmailV1 = offer.combinedOfferData?.purchaseAgreement?.buyerAgent?.agentEmail;
    const agentNameV2 = offer.extractedData?.agents?.buyer_broker?.agent_name;
    const agentNameV1 = offer.combinedOfferData?.purchaseAgreement?.buyerAgent?.agentName
      || offer.combinedOfferData?.purchaseAgreement?.buyer?.agentName;
    const sellingAgent = {
      agentEmail: agentEmailV2 || agentEmailV1,
      agentName: agentNameV2 || agentNameV1,
      agentPhone: offer.extractedData?.agents?.buyer_broker?.phone
        || offer.combinedOfferData?.purchaseAgreement?.buyerAgent?.agentPhone,
    };
    if (!sellingAgent.agentEmail) return;
    
    if (rating === 0) {
      alert('Please select a rating');
      return;
    }

    try {
      setSavingNote(true);
      const now = Timestamp.now();

      // Check if buyer agent is a registered user
      let buyerAgentUserId: string | undefined;
      try {
        const usersQuery = query(
          collection(db, 'users'),
          where('email', '==', sellingAgent.agentEmail)
        );
        const usersSnapshot = await getDocs(usersQuery);
        if (!usersSnapshot.empty) {
          buyerAgentUserId = usersSnapshot.docs[0].id;
        }
      } catch (error) {
        console.error('Error checking buyer agent user:', error);
      }

      // Get listing agent (current user) profile
      let listingAgentEmail: string | undefined;
      let listingAgentName: string | undefined;
      try {
        const userProfileDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));
        if (userProfileDoc.exists()) {
          const profile = userProfileDoc.data();
          listingAgentEmail = profile.email;
          listingAgentName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
        }
      } catch (error) {
        console.error('Error fetching listing agent profile:', error);
      }

      if (agentNote?.id) {
        // Update existing note
        const updateData: any = {
          rating,
          note: noteText,
          updatedAt: now
        };
        
        if (buyerAgentUserId) {
          updateData.buyerAgentUserId = buyerAgentUserId;
        }
        
        await updateDoc(doc(db, 'agentNotes', agentNote.id), updateData);
        
        setAgentNote({
          ...agentNote,
          rating,
          note: noteText,
          buyerAgentUserId: buyerAgentUserId || agentNote.buyerAgentUserId,
          updatedAt: now
        });
      } else {
        // Create new note - only include defined fields
        const newNote: any = {
          createdByUserId: currentUser.uid,
          buyerAgentEmail: sellingAgent.agentEmail,
          buyerAgentName: sellingAgent.agentName || 'Unknown Agent',
          rating,
          note: noteText,
          createdAt: now,
          updatedAt: now
        };
        
        // Only add optional fields if they have values
        if (buyerAgentUserId) {
          newNote.buyerAgentUserId = buyerAgentUserId;
        }
        if (listingAgentEmail) {
          newNote.listingAgentEmail = listingAgentEmail;
        }
        if (listingAgentName) {
          newNote.listingAgentName = listingAgentName;
        }
        
        const docRef = await addDoc(collection(db, 'agentNotes'), newNote);
        setAgentNote({ id: docRef.id, ...newNote } as AgentNote);
      }
      
      setShowNoteForm(false);
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Failed to save note. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleCancelNote = () => {
    if (agentNote) {
      setNoteText(agentNote.note);
      setRating(agentNote.rating);
    } else {
      setNoteText('');
      setRating(0);
    }
    setShowNoteForm(false);
  };

  const formatNoteDate = (timestamp: Timestamp) => {
    try {
      return timestamp.toDate().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const calculateLTV = (loan: any): number | null => {
    // If LTV is already provided, use it
    if (loan?.loanToValue) {
      return loan.loanToValue;
    }

    // Calculate LTV from purchase price and down payment
    if (loan?.purchasePrice && loan?.downPayment) {
      const loanAmount = loan.purchasePrice - loan.downPayment;
      return Math.round((loanAmount / loan.purchasePrice) * 100);
    }

    // Calculate LTV from purchase price and loan amount
    if (loan?.purchasePrice && loan?.loanAmount) {
      return Math.round((loan.loanAmount / loan.purchasePrice) * 100);
    }

    return null;
  };

  const calculatePurchasePrice = (loan: any): number | null => {
    // If purchase price is already provided, use it
    if (loan?.purchasePrice) {
      return loan.purchasePrice;
    }

    // Calculate from down payment and LTV
    if (loan?.downPayment && loan?.loanToValue) {
      const downPaymentPercentage = 100 - loan.loanToValue;
      return Math.round(loan.downPayment / (downPaymentPercentage / 100));
    }

    return null;
  };

  const fetchLenderExperience = async (nmlsId: string | number) => {
    setLenderExperienceLoading(true);
    try {
      // Query lenders by nmlsId integer field — most docs use auto-generated IDs, not nmlsId as doc ID
      const nmlsIdInt = parseInt(nmlsId.toString(), 10);
      const lendersSnap = await getDocs(
        query(collection(db, 'lenders'), where('nmlsId', '==', nmlsIdInt))
      );

      if (!lendersSnap.empty) {
        const data = lendersSnap.docs[0].data();
        // yearsExperience written by backfill; experienceYears by lenderWriter.ts
        const experience = data.yearsExperience ?? data.experienceYears ?? null;
        setLenderExperience(experience);
      } else {
        setLenderExperience(null);
      }
    } catch (error) {
      console.error('Error fetching lender experience:', error);
      setLenderExperience(null);
    } finally {
      setLenderExperienceLoading(false);
      setLenderExperienceReady(true);
    }
  };

  // Live PTC score — always computed from current extractedData + lenderExperience
  // so the display is never stale regardless of what's stored in Firestore.
  const liveScore = useMemo((): number | null => {
    if (!offer?.extractedData) return typeof offer?.ptcScore === 'number' ? offer.ptcScore : null;
    const input = buildPtcInputFromOffer(offer.extractedData, lenderExperience ?? undefined);
    return calculatePtcScore(input).ptcScore;
  }, [offer?.extractedData, lenderExperience]);

  // Save the correct score back to Firestore once lender experience is fully resolved.
  // Also updates local offer state so every display point in this page reflects
  // the same value that is now stored — no more "different score each visit".
  useEffect(() => {
    if (!offer?.id || liveScore === null || !offer.extractedData) return;
    if (!lenderExperienceReady || lenderExperienceLoading) return;
    const stored = offer.ptcScore;
    if (typeof stored === 'number' && Math.abs(stored - liveScore) < 0.01) return;

    const update: { ptcScore: number; ptcCalculatedAt: any; lenderExperienceYears?: number } = {
      ptcScore: liveScore,
      ptcCalculatedAt: Timestamp.now(),
    };
    if (lenderExperience !== null) update.lenderExperienceYears = lenderExperience;

    updateDoc(doc(db, 'offers', offer.id), update)
      .then(() => {
        // Sync local state so the displayed score matches what was just written to Firestore
        setOffer(prev => prev ? { ...prev, ptcScore: liveScore, lenderExperienceYears: lenderExperience ?? undefined } : prev);
      })
      .catch(err => console.warn('Auto-save PTC score failed:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveScore, offer?.id, lenderExperienceReady, lenderExperienceLoading]);


  const handlePreApprovalFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    setUploadSuccess(false);
    
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      setUploadError('Please upload a PDF file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size must be less than 10MB');
      return;
    }

    setPreApprovalFile(file);
  };

  const handlePreApprovalUpload = async () => {
    if (!preApprovalFile || !offer || !id) return;

    setUploadingPreApproval(true);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      // Upload to Firebase Storage
      const timestamp = Date.now();
      const fileName = `preapproval-${timestamp}.pdf`;
      const storagePath = `offers/${id}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, preApprovalFile);
      const downloadURL = await getDownloadURL(storageRef);

      // Update offer-documents collection
      const offerDocRef = doc(db, 'offer-documents', id);
      await updateDoc(offerDocRef, {
        files: arrayUnion({
          fileName,
          storagePath,
          downloadURL,
          uploadedAt: Timestamp.now(),
          type: 'application/pdf',
          processed: false,
        }),
        updatedAt: Timestamp.now(),
      });

      // Trigger reprocessing by updating the offer
      const offerRef = doc(db, 'offers', id);
      await updateDoc(offerRef, {
        parsingStatus: 'pending',
        processingCompleted: false,
        updatedAt: Timestamp.now(),
        preApprovalAddedManually: true,
      });

      setUploadSuccess(true);
      setPreApprovalFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('preapproval-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      // Poll for processing completion before refreshing
      const checkProcessingComplete = async () => {
        const offerRef = doc(db, 'offers', id);
        const maxAttempts = 30; // 30 seconds max wait
        let attempts = 0;

        const pollInterval = setInterval(async () => {
          attempts++;
          try {
            const offerSnapshot = await getDoc(offerRef);
            const offerData = offerSnapshot.data();
            
            // Check if processing is complete and pre-approval has data (v2 or v1 schema)
            const hasPreApprovalV2 = Boolean(offerData?.extractedData?.pre_approval?.lender_name || offerData?.extractedData?.pre_approval?.loan_officer_nmls);
            if (offerData?.processingCompleted && (hasPreApprovalV2 || offerData?.combinedOfferData?.preApproval?.hasData === true)) {
              clearInterval(pollInterval);
              window.location.reload();
            } else if (attempts >= maxAttempts) {
              // Timeout - reload anyway
              clearInterval(pollInterval);
              window.location.reload();
            }
          } catch (error) {
            console.error('Error checking processing status:', error);
            if (attempts >= maxAttempts) {
              clearInterval(pollInterval);
              window.location.reload();
            }
          }
        }, 1000); // Check every second
      };

      checkProcessingComplete();

    } catch (error) {
      console.error('Error uploading pre-approval:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to upload pre-approval letter');
    } finally {
      setUploadingPreApproval(false);
    }
  };

  if (loading) {
    return (
      <div className="offer-detail-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading offer...</p>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="offer-detail-container">
        <div className="empty-state">
          <h3>Offer Not Found</h3>
          <p>The offer you're looking for doesn't exist or has been deleted.</p>
          <button className="primary-button" onClick={() => navigate('/offers')}>
            View All Offers
          </button>
        </div>
      </div>
    );
  }

  const { preApprovalData, combinedOfferData, extractedData } = offer;

  // Check if we have purchase agreement data (v1 schema)
  const purchaseAgreement = combinedOfferData?.purchaseAgreement?.hasData
    ? combinedOfferData.purchaseAgreement
    : null;

  // Get pre-approval data from the correct source (v1 schema)
  const preApproval = combinedOfferData?.preApproval?.hasData
    ? combinedOfferData.preApproval
    : preApprovalData; // Fallback to legacy field

  // v2 schema presence flags
  const hasExtractedData = Boolean(extractedData);
  const hasExtractedPreApproval = hasExtractedData && Boolean(extractedData?.pre_approval?.lender_name || extractedData?.pre_approval?.loan_officer_nmls);
  const hasExtractedPurchaseAgreement = hasExtractedData && Boolean(extractedData?.transaction?.final_sales_price || extractedData?.property?.address);

  return (
    <div className="offer-detail-container">
      <div className="offer-detail-header">
        <button className="back-button" onClick={() => navigate('/offers')}>
          ← Back to Offers
        </button>
        <div className="header-title-row">
          <div>
            <h2>Offer Details</h2>
            <p>Submitted on {formatDate(offer.submissionDate)}</p>
          </div>
          {offer.submittedVia && (
            <span className="submission-badge">
              {offer.submittedVia === 'shared-link' 
                ? '🔗 Submitted via Shareable Link' 
                : offer.submittedVia === 'email'
                ? '📧 Submitted via Email'
                : '📋 Submitted by Agent'}
            </span>
          )}
        </div>
      </div>

      {/* Agent, Lender & Property Info Section */}
      <div className="detail-section party-property-section">
        {/* Property Address Above Cards */}
        {(() => {
          const propertyAddress = listing?.address || extractedData?.property?.address || purchaseAgreement?.property?.address;
          const propertyCity = listing?.city || extractedData?.property?.city || purchaseAgreement?.property?.city;
          const propertyState = listing?.state || extractedData?.property?.state || purchaseAgreement?.property?.state;
          const propertyZip = listing?.zipCode || extractedData?.property?.zip || purchaseAgreement?.property?.zipCode;
          
          if (propertyAddress) {
            return (
              <div className="property-address-above">
                <div className="property-address-line">
                  🏠 {propertyAddress}
                  {propertyCity && `, ${propertyCity}`}
                  {propertyState && `, ${propertyState}`}
                  {propertyZip && ` ${propertyZip}`}
                </div>
                {(listing?.mlsNumber || listing?.listingPrice) && (
                  <div className="property-listing-chips">
                    {listing.mlsNumber && (
                      <span className="property-listing-chip chip-mls">MLS# {listing.mlsNumber}</span>
                    )}
                    {listing.listingPrice && (
                      <span className="property-listing-chip chip-lp">LP {formatCurrency(listing.listingPrice)}</span>
                    )}
                  </div>
                )}
              </div>
            );
          }
          return null;
        })()}

        <div className="parties-row">
          {(() => {
            // v2 schema first, then v1 fallback for selling agent
            const sellingAgent = {
              agentName: extractedData?.agents?.buyer_broker?.agent_name
                || purchaseAgreement?.buyerAgent?.agentName
                || purchaseAgreement?.buyer?.agentName,
              agentId: extractedData?.agents?.buyer_broker?.agent_id
                || purchaseAgreement?.buyerAgent?.agentId
                || purchaseAgreement?.buyer?.agentId,
              agentEmail: extractedData?.agents?.buyer_broker?.email
                || purchaseAgreement?.buyerAgent?.agentEmail,
              agentPhone: extractedData?.agents?.buyer_broker?.phone
                || purchaseAgreement?.buyerAgent?.agentPhone,
            };

            // v2 schema first, then v1 fallback for lender
            const resolvedLender = {
              name: extractedData?.pre_approval?.lender_name || preApproval?.lender?.name,
              loanOfficerName: extractedData?.pre_approval?.loan_officer_name || preApproval?.lender?.loanOfficerName,
              nmlsId: extractedData?.pre_approval?.loan_officer_nmls || preApproval?.lender?.nmlsId,
              email: preApproval?.lender?.email,
              phone: preApproval?.lender?.phone,
            };
            const hasLender = Boolean(resolvedLender.name || resolvedLender.loanOfficerName || resolvedLender.nmlsId);

            return (
              <>
                {sellingAgent.agentName && (
                  <div className="party-card">
                    <div className="party-card-header">
                      <span className="party-card-label">Selling Agent</span>
                      {currentUser && sellingAgent.agentEmail && (
                        <button
                          className="agent-notes-badge-compact"
                          onClick={() => {
                            if (sellingAgent.agentEmail) {
                              fetchAgentNote(sellingAgent.agentEmail);
                            }
                            setShowNotesModal(true);
                          }}
                          title={agentNote ? `Your rating: ${agentNote.rating}/5 stars` : "Add notes about this agent"}
                        >
                          <span className="notes-icon-compact">📝</span>
                          {agentNote ? (
                            <div className="notes-rating-compact">
                              {[...Array(5)].map((_, i) => (
                                <span key={i} className={`mini-star ${i < agentNote.rating ? 'filled' : ''}`}>★</span>
                              ))}
                            </div>
                          ) : (
                            <span className="notes-text-compact">Add Notes</span>
                          )}
                        </button>
                      )}
                    </div>
                    <span className="party-card-name">{sellingAgent.agentName}</span>
                    {sellingAgent.agentId && (
                      <span className="party-card-detail">Lic# {sellingAgent.agentId}</span>
                    )}
                    {sellingAgent.agentEmail && (
                      <span className="party-card-detail">📧 {sellingAgent.agentEmail}</span>
                    )}
                    {sellingAgent.agentPhone && (
                      <span className="party-card-detail">📞 {sellingAgent.agentPhone}</span>
                    )}
                  </div>
                )}
                {hasLender && (
                  <div className="party-card">
                    <span className="party-card-label">Lender</span>
                    <span className="party-card-name">{resolvedLender.name || resolvedLender.loanOfficerName || 'N/A'}</span>
                    {(resolvedLender.loanOfficerName || resolvedLender.nmlsId) && (
                      <span className="party-card-detail nmls-highlight">
                        {resolvedLender.loanOfficerName && resolvedLender.name && (
                          <span>👤 {resolvedLender.loanOfficerName}</span>
                        )}
                        {resolvedLender.loanOfficerName && resolvedLender.name && resolvedLender.nmlsId && (
                          <span> · </span>
                        )}
                        {resolvedLender.nmlsId && (
                          <>
                            NMLS# {resolvedLender.nmlsId}
                            {lenderExperienceLoading && <span className="lender-experience-badge loading">...</span>}
                            {!lenderExperienceLoading && lenderExperience !== null && (
                              <span className="lender-experience-badge">{lenderExperience}yrs</span>
                            )}
                            {!lenderExperienceLoading && lenderExperience === null && (
                              <span className="lender-experience-badge pending" title="Lender verification pending">❓</span>
                            )}
                          </>
                        )}
                      </span>
                    )}
                    {resolvedLender.email && (
                      <span className="party-card-detail">📧 {resolvedLender.email}</span>
                    )}
                    {resolvedLender.phone && (
                      <span className="party-card-detail">📞 {resolvedLender.phone}</span>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Offer Score & Recommendations Section */}
      <div className="detail-section score-recommendations-section">
        <div className="offer-summary-header">
          <div className="offer-summary-info">
            <div className="offer-loan-details">
              {(() => {
                // v2 schema (extractedData) first, then v1 fallbacks
                const purchasePrice = extractedData?.transaction?.final_sales_price
                  ?? purchaseAgreement?.financial?.purchasePrice
                  ?? preApproval?.loan?.purchasePrice
                  ?? calculatePurchasePrice(preApproval?.loan);
                const loanAmount = extractedData?.financing?.mortgage_amount
                  ?? purchaseAgreement?.financial?.loanAmount
                  ?? preApproval?.loan?.loanAmount;
                const downPayment = extractedData?.financing?.down_payment_amount
                  ?? purchaseAgreement?.financial?.downPayment
                  ?? preApproval?.loan?.downPayment;
                const ltv = calculateLTV(preApproval?.loan);
                const earnestMoney = extractedData?.transaction?.earnest_money_deposit
                  ?? purchaseAgreement?.financial?.earnestMoneyTotal;
                const sellerConcessions = extractedData?.financing?.seller_concession_amount
                  ?? purchaseAgreement?.financial?.sellerConcessions;
                
                return (
                  <>
                    {purchasePrice !== null && purchasePrice !== undefined && (
                      <span className="loan-detail">
                        <strong>Purchase:</strong> {formatCurrency(purchasePrice)}
                      </span>
                    )}
                    {loanAmount !== null && loanAmount !== undefined && (
                      <span className="loan-detail">
                        <strong>Loan:</strong> {formatCurrency(loanAmount)}
                      </span>
                    )}
                    {downPayment !== null && downPayment !== undefined && (
                      <span className="loan-detail">
                        <strong>Down:</strong> {formatCurrency(downPayment)}
                      </span>
                    )}
                    {earnestMoney !== null && earnestMoney !== undefined && (
                      <span className="loan-detail">
                        <strong>EMD:</strong> {formatCurrency(earnestMoney)}
                      </span>
                    )}
                    {ltv !== null && ltv !== undefined && (
                      <span className="loan-detail">
                        <strong>LTV:</strong> {ltv}%
                      </span>
                    )}
                    {(extractedData?.financing?.mortgage_type || preApproval?.loan?.loanType) && (
                      <span className="loan-detail">
                        <strong>Type:</strong> {extractedData?.financing?.mortgage_type || preApproval?.loan?.loanType}
                      </span>
                    )}
                    {sellerConcessions !== null && sellerConcessions !== undefined && (
                      <span className="loan-detail">
                        <strong>Concessions:</strong> {formatCurrency(sellerConcessions)}
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
 
        {liveScore !== null && (
          <div className={`offer-score-display ${
            liveScore >= 70 ? 'score-green' :
            liveScore >= 50 ? 'score-yellow' :
            'score-red'
          }`}>
            <div className="score-container">
              <div className="score-circle">
                <div className="score-number">{Math.round(liveScore)}</div>
                <div className="score-label">PTC Score</div>
              </div>
              <div className="score-insight">
                <p className="score-message">
                  {liveScore >= 80 ? '🎉 Excellent! This offer is highly competitive' :
                   liveScore >= 70 ? '✨ Strong offer with great potential' :
                   liveScore >= 60 ? '👍 Good offer, competitive position' :
                   liveScore >= 50 ? '💭 Fair offer, some improvements recommended' :
                   liveScore >= 40 ? '⚠️ Needs improvement to be more competitive' :
                   '🔴 Challenging offer, significant improvements needed'}
                </p>
                <p className="score-description">
                  This score reflects the probability of this offer closing successfully based on financial strength, transactional risk, and professional vetting.
                </p>
                {offer.ptcRecommendation && (
                  <div className="ptc-recommendation-box">
                    <h4 className="recommendation-title">📋 Recommendation</h4>
                    <p className="recommendation-text">{offer.ptcRecommendation}</p>
                  </div>
                )}
                <button className="edit-offer-btn" onClick={() => setShowEditModal(true)}>
                  ✏️ Edit Offer Details
                </button>
              </div>
            </div>
          </div>
        )}

        {offer.recommendations && offer.recommendations.length > 0 && (
          <div className="recommendations-preview">
            <div className="recommendations-header">
              <h4>💡 Recommendations to Improve This Offer</h4>
              <span className="recommendations-count">{offer.recommendations.length} recommendation(s)</span>
            </div>
            <ul className="recommendations-list">
              {offer.recommendations.slice(0, 3).map((recommendation, idx) => (
                <li key={idx}>{recommendation}</li>
              ))}
            </ul>
            {offer.recommendations.length > 3 && (
              <button 
                className="view-all-recommendations-btn"
                onClick={() => setShowRecommendations(true)}
              >
                View All {offer.recommendations.length} Recommendations
              </button>
            )}
          </div>
        )}
      </div>

      {/* Agent Notes Modal */}
      {showNotesModal && currentUser && (() => {
        // v2 schema first, then v1 fallback
        const modalAgent = {
          agentEmail: extractedData?.agents?.buyer_broker?.email
            || purchaseAgreement?.buyerAgent?.agentEmail,
          agentName: extractedData?.agents?.buyer_broker?.agent_name
            || purchaseAgreement?.buyerAgent?.agentName
            || purchaseAgreement?.buyer?.agentName,
          agentPhone: extractedData?.agents?.buyer_broker?.phone
            || purchaseAgreement?.buyerAgent?.agentPhone,
        };
        return modalAgent.agentEmail ? (
        <div className="modal-overlay" onClick={() => setShowNotesModal(false)}>
          <div className="agent-notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-section">
                <h3>📝 Notes & Rating</h3>
                <div className="agent-info-compact">
                  <strong>{modalAgent.agentName}</strong>
                  <span className="agent-email">{modalAgent.agentEmail}</span>
                  {modalAgent.agentPhone && <span className="agent-phone">{modalAgent.agentPhone}</span>}
                </div>
              </div>
              <button 
                className="modal-close"
                onClick={() => setShowNotesModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-content">
              {!showNoteForm && (
                <button 
                  className="btn-secondary add-note-btn"
                  onClick={() => setShowNoteForm(true)}
                >
                  {agentNote ? '✏️ Edit Note' : '+ Add Note'}
                </button>
              )}

              {!showNoteForm && agentNote && (
              <div className="agent-note-display">
                <div className="note-header">
                  <div className="agent-rating">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span 
                        key={star}
                        className={`star ${star <= agentNote.rating ? 'filled' : ''}`}
                      >
                        ★
                      </span>
                    ))}
                    <span className="rating-text">({agentNote.rating}/5)</span>
                  </div>
                  <span className="note-date">
                    {agentNote.updatedAt.toDate().getTime() !== agentNote.createdAt.toDate().getTime() 
                      ? `Updated ${formatNoteDate(agentNote.updatedAt)}`
                      : `Added ${formatNoteDate(agentNote.createdAt)}`
                    }
                  </span>
                </div>
                <div className="note-content">
                  {agentNote.note || <em className="no-note">No written notes</em>}
                </div>
              </div>
            )}

            {!showNoteForm && !agentNote && (
              <div className="no-note-placeholder">
                <p>You haven't added any notes or rating for {modalAgent.agentName} yet.</p>
                <p className="note-hint">Add notes to remember your experience working with this agent for future reference.</p>
              </div>
            )}

            {showNoteForm && (
              <div className="agent-note-form">
                {agentNote && (
                  <div className="note-update-info">
                    Last updated: {formatNoteDate(agentNote.updatedAt)}
                  </div>
                )}
                <div className="form-group">
                  <label>Rating <span className="required">*</span></label>
                  <div className="star-rating-input">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span
                        key={star}
                        className={`star ${star <= (hoverRating || rating) ? 'filled' : ''}`}
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                      >
                        ★
                      </span>
                    ))}
                    {rating > 0 && (
                      <span className="rating-label">
                        {rating === 1 && 'Poor'}
                        {rating === 2 && 'Fair'}
                        {rating === 3 && 'Good'}
                        {rating === 4 && 'Very Good'}
                        {rating === 5 && 'Excellent'}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="agentNote">Notes (Optional)</label>
                  <textarea
                    id="agentNote"
                    rows={5}
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add your notes about working with this agent, their professionalism, communication, offer quality, etc."
                    maxLength={1000}
                  />
                  <div className="char-count">{noteText.length}/1000</div>
                </div>

                <div className="form-actions">
                  <button 
                    className="btn-primary"
                    onClick={handleSaveNote}
                    disabled={savingNote || rating === 0}
                  >
                    {savingNote ? 'Saving...' : (agentNote ? 'Update Note' : 'Save Note')}
                  </button>
                  <button 
                    className="btn-cancel"
                    onClick={handleCancelNote}
                    disabled={savingNote}
                  >
                    Cancel
                  </button>
                </div>

                <p className="privacy-note">
                  <strong>Privacy:</strong> These notes are private and only visible to you. They won't be shared with the agent or anyone else.
                </p>
              </div>
            )}
            </div>
          </div>
        </div>
        ) : null;
      })()}

      <div className="detail-section files-section">
        <div className="files-header">
          <h3>📄 Submitted Documents</h3>
        </div>
        
        {(() => {
          const hasPurchaseAgreement = hasExtractedPurchaseAgreement || combinedOfferData?.purchaseAgreement?.hasData;
          const hasPreApproval = hasExtractedPreApproval || combinedOfferData?.preApproval?.hasData;
          const purchaseAgreementFile = combinedOfferData?.purchaseAgreement?.hasData ? combinedOfferData.purchaseAgreement.sourceFile : null;
          const preApprovalFile = combinedOfferData?.preApproval?.hasData ? combinedOfferData.preApproval.sourceFile : null;
          
          // Check if files are combined (same sourceFile for both)
          const isCombinedFile = hasPreApproval && hasPurchaseAgreement && 
                                 preApprovalFile && purchaseAgreementFile && 
                                 preApprovalFile === purchaseAgreementFile;
          
          // Authoritative download URL lookup: offer.files (main document) always has
          // downloadURL set by uploadFile. offer-documents.files can have downloadURL: null.
          const getDownloadUrl = (fileName: string): string =>
            offer?.files?.find(f => f.fileName === fileName)?.downloadURL
            || files.find(f => f.fileName === fileName)?.downloadURL
            || '';

          // Get files
          const paFiles = purchaseAgreementFile
            ? files.filter(f => f.fileName === purchaseAgreementFile)
            : (hasPurchaseAgreement ? files.filter(f => !f.fileName.toLowerCase().includes('preapproval') && !f.fileName.toLowerCase().includes('pre-approval')) : []);
          
          const preApprovalFiles = preApprovalFile 
            ? files.filter(f => f.fileName === preApprovalFile)
            : (hasPreApproval ? files.filter(f => f.fileName.toLowerCase().includes('preapproval') || f.fileName.toLowerCase().includes('pre-approval')) : []);
          
          // Determine document label
          let documentLabel = '';
          if (isCombinedFile) {
            documentLabel = 'Purchase Agreement & Pre-Approval';
          } else if (hasPreApproval && hasPurchaseAgreement) {
            documentLabel = 'Purchase Agreement & Pre-Approval';
          } else if (hasPurchaseAgreement) {
            documentLabel = 'Purchase Agreement';
          } else if (hasPreApproval) {
            documentLabel = 'Pre-Approval';
          }
          
          return (
            <div className="files-compact-list">
              <div className="file-category-label">
                <span className="category-dot"></span>
                {documentLabel}
              </div>
              
              {/* Combined file - show single file */}
              {isCombinedFile && paFiles.length > 0 ? (
                paFiles.map((file, index) => (
                  <div key={index} className="file-row">
                    <div className="file-left">
                      <span className="file-icon-small">📄</span>
                      <span className="file-name-compact">{file.fileName}</span>
                      {file.processed && <span className="status-dot success" title="Processed">✓</span>}
                      {file.error && <span className="status-dot error" title={file.error}>⚠</span>}
                    </div>
                    <a
                      href={getDownloadUrl(file.fileName)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="download-link"
                    >
                      Download
                    </a>
                  </div>
                ))
              ) : !isCombinedFile && (hasPreApproval || hasPurchaseAgreement) ? (
                <>
                  {/* Separate files - show both */}
                  {paFiles.map((file, index) => (
                    <div key={`pa-${index}`} className="file-row">
                      <div className="file-left">
                        <span className="file-icon-small">📃</span>
                        <span className="file-name-compact">{file.fileName}</span>
                        {file.processed && <span className="status-dot success" title="Processed">✓</span>}
                        {file.error && <span className="status-dot error" title={file.error}>⚠</span>}
                      </div>
                      <a
                        href={getDownloadUrl(file.fileName)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="download-link"
                      >
                        Download
                      </a>
                    </div>
                  ))}

                  {preApprovalFiles.map((file, index) => (
                    <div key={`pre-${index}`} className="file-row">
                      <div className="file-left">
                        <span className="file-icon-small">📋</span>
                        <span className="file-name-compact">{file.fileName}</span>
                        {file.processed && <span className="status-dot success" title="Processed">✓</span>}
                        {file.error && <span className="status-dot error" title={file.error}>⚠</span>}
                      </div>
                      <a
                        href={getDownloadUrl(file.fileName)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="download-link"
                      >
                        Download
                      </a>
                    </div>
                  ))}
                </>
              ) : (
                <div className="file-row no-files">
                  <span className="file-icon-small">📄</span>
                  <span className="file-name-compact">No documents found</span>
                </div>
              )}
            </div>
          );
        })()}

        {/* Missing Pre-Approval Message - Show only when no pre-approval data exists in v2 or v1 schema */}
        {offer && !hasExtractedPreApproval && offer.combinedOfferData && !offer.combinedOfferData.preApproval?.hasData && (
          <div className="pre-approval-missing-section" style={{ 
            marginTop: '20px', 
            padding: '16px', 
            background: '#fff3cd', 
            border: '1px solid #ffc107', 
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
            <div>
              <div style={{ fontWeight: '600', color: '#856404', marginBottom: '4px' }}>
                Missing Pre-Approval Letter
              </div>
              <div style={{ fontSize: '14px', color: '#856404' }}>
                Upload a pre-approval letter to improve your PTC score and offer competitiveness.
              </div>
            </div>
          </div>
        )}

        {/* Pre-Approval Upload Section - Show only if pre-approval is missing in both v2 and v1 schema */}
        {offer && !hasExtractedPreApproval && offer.combinedOfferData && !offer.combinedOfferData.preApproval?.hasData && (
          <div className="pre-approval-upload-section">
            <div className="upload-header">
              <h3>📄 Upload Pre-Approval Letter</h3>
              <p>Add your pre-approval letter to improve your PTC score and offer competitiveness.</p>
            </div>
            
            {uploadSuccess ? (
              <div className="upload-success-message">
                <div className="processing-spinner"></div>
                <div>
                  <div className="success-title">✅ Pre-approval letter uploaded successfully!</div>
                  <div className="processing-text">Reprocessing offer and recalculating PTC score...</div>
                </div>
              </div>
            ) : (
              <div className="upload-controls">
                <div className="file-input-wrapper">
                  <input
                    type="file"
                    id="preapproval-upload"
                    accept="application/pdf"
                    onChange={handlePreApprovalFileSelect}
                    disabled={uploadingPreApproval}
                  />
                  <label htmlFor="preapproval-upload" className="file-input-label">
                    {preApprovalFile ? `📎 ${preApprovalFile.name}` : '📁 Choose PDF File'}
                  </label>
                </div>
                
                {preApprovalFile && (
                  <button
                    className="upload-button"
                    onClick={handlePreApprovalUpload}
                    disabled={uploadingPreApproval}
                  >
                    {uploadingPreApproval ? 'Uploading...' : 'Upload Pre-Approval'}
                  </button>
                )}
              </div>
            )}

            {uploadError && (
              <div className="upload-error-message">
                ⚠️ {uploadError}
              </div>
            )}
          </div>
        )}
      </div>

      {(hasExtractedPreApproval || preApproval?.lender || preApproval?.mortgageCompany) && (
        <div className="detail-section">
          <h3>🏦 Lender & Mortgage Company</h3>
          <div className="detail-grid">
            {/* v2 schema fields */}
            {extractedData?.pre_approval?.loan_officer_name && (
              <div className="detail-item">
                <span className="detail-label">Loan Officer</span>
                <span className="detail-value">{extractedData.pre_approval.loan_officer_name}</span>
              </div>
            )}
            {!extractedData?.pre_approval?.loan_officer_name && (preApproval?.lender?.loanOfficerName || preApproval?.lender?.name) && (
              <div className="detail-item">
                <span className="detail-label">Loan Officer</span>
                <span className="detail-value">{preApproval?.lender?.loanOfficerName || preApproval?.lender?.name}</span>
              </div>
            )}
            {extractedData?.pre_approval?.lender_name && (
              <div className="detail-item">
                <span className="detail-label">Lender Name</span>
                <span className="detail-value">{extractedData.pre_approval.lender_name}</span>
              </div>
            )}
            {(extractedData?.pre_approval?.loan_officer_nmls || preApproval?.lender?.nmlsId) && (
              <div className="detail-item">
                <span className="detail-label">Loan Officer NMLS#</span>
                <span className="detail-value highlight">
                  {extractedData?.pre_approval?.loan_officer_nmls || preApproval?.lender?.nmlsId}
                  {lenderExperienceLoading && <span className="lender-experience-badge loading">...</span>}
                  {!lenderExperienceLoading && lenderExperience !== null && (
                    <span className="lender-experience-badge">{lenderExperience}yrs</span>
                  )}
                  {!lenderExperienceLoading && lenderExperience === null && (
                    <span className="lender-experience-badge pending" title="Lender verification pending">❓</span>
                  )}
                </span>
              </div>
            )}
            {preApproval?.lender?.phone && (
              <div className="detail-item">
                <span className="detail-label">Phone</span>
                <span className="detail-value">{preApproval.lender.phone}</span>
              </div>
            )}
            {preApproval?.lender?.email && (
              <div className="detail-item">
                <span className="detail-label">Email</span>
                <span className="detail-value">{preApproval.lender.email}</span>
              </div>
            )}
            {preApproval?.mortgageCompany?.name && (
              <div className="detail-item">
                <span className="detail-label">Company Name</span>
                <span className="detail-value">{preApproval.mortgageCompany.name}</span>
              </div>
            )}
            {preApproval?.mortgageCompany?.nmlsId && (
              <div className="detail-item">
                <span className="detail-label">Company NMLS#</span>
                <span className="detail-value highlight">{preApproval.mortgageCompany.nmlsId}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* v2 Schema: Extracted Purchase Agreement & Transaction Data */}
      {hasExtractedPurchaseAgreement && (
        <>
          <div className="detail-section purchase-agreement-header">
            <h2>📋 Purchase Agreement Details</h2>
          </div>

          {/* Property */}
          {extractedData?.property && (
            <div className="detail-section">
              <h3>🏡 Property Information</h3>
              <div className="detail-grid">
                <div className="detail-item full-width">
                  <span className="detail-label">Address</span>
                  <span className="detail-value highlight">
                    {extractedData.property.address || 'N/A'}
                    {extractedData.property.city && `, ${extractedData.property.city}`}
                    {extractedData.property.state && `, ${extractedData.property.state}`}
                    {extractedData.property.zip && ` ${extractedData.property.zip}`}
                  </span>
                </div>
                {extractedData.property.county && (
                  <div className="detail-item">
                    <span className="detail-label">County</span>
                    <span className="detail-value">{extractedData.property.county}</span>
                  </div>
                )}
                {extractedData.property.tax_id && (
                  <div className="detail-item">
                    <span className="detail-label">Tax ID / APN</span>
                    <span className="detail-value">{extractedData.property.tax_id}</span>
                  </div>
                )}
                {extractedData.property.legal_description && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Legal Description</span>
                    <span className="detail-value">{extractedData.property.legal_description}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Transaction / Financial Terms */}
          {(extractedData?.transaction || extractedData?.financing) && (
            <div className="detail-section">
              <h3>💵 Financial Terms</h3>
              <div className="detail-grid">
                {extractedData?.transaction?.final_sales_price && (
                  <div className="detail-item">
                    <span className="detail-label">Purchase Price</span>
                    <div>
                      <span className="detail-value highlight">{formatCurrency(extractedData.transaction.final_sales_price)}</span>
                      {(extractedData.escalation_clause?.present || extractedData.appraisal_guarantee?.present) && (
                        <div className="price-clause-badges">
                          {extractedData.escalation_clause?.present && (
                            <span className="price-clause-badge price-clause-escalation">
                              ⚡ Escalation
                              {extractedData.escalation_clause.max_cap != null && (
                                <span className="price-clause-detail"> · Cap {formatCurrency(extractedData.escalation_clause.max_cap)}</span>
                              )}
                            </span>
                          )}
                          {extractedData.appraisal_guarantee?.present && (
                            <span className="price-clause-badge price-clause-appraisal">
                              🛡️ Appraisal Gap
                              {extractedData.appraisal_guarantee.coverage_amount != null && (
                                <span className="price-clause-detail"> · {formatCurrency(extractedData.appraisal_guarantee.coverage_amount)}</span>
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {extractedData?.transaction?.earnest_money_deposit && (
                  <div className="detail-item">
                    <span className="detail-label">Earnest Money</span>
                    <span className="detail-value">{formatCurrency(extractedData.transaction.earnest_money_deposit)}</span>
                  </div>
                )}
                {extractedData?.financing?.down_payment_amount && (
                  <div className="detail-item">
                    <span className="detail-label">Down Payment</span>
                    <span className="detail-value">{formatCurrency(extractedData.financing.down_payment_amount)}</span>
                  </div>
                )}
                {extractedData?.financing?.down_payment_percent && (
                  <div className="detail-item">
                    <span className="detail-label">Down Payment %</span>
                    <span className="detail-value">{extractedData.financing.down_payment_percent}%</span>
                  </div>
                )}
                {extractedData?.financing?.mortgage_amount && (
                  <div className="detail-item">
                    <span className="detail-label">Loan Amount</span>
                    <span className="detail-value">{formatCurrency(extractedData.financing.mortgage_amount)}</span>
                  </div>
                )}
                {extractedData?.financing?.mortgage_type && (
                  <div className="detail-item">
                    <span className="detail-label">Loan Type</span>
                    <span className="detail-value">{extractedData.financing.mortgage_type}</span>
                  </div>
                )}
                {extractedData?.financing?.seller_concession_amount && (
                  <div className="detail-item">
                    <span className="detail-label">Seller Concessions</span>
                    <span className="detail-value">{formatCurrency(extractedData.financing.seller_concession_amount)}</span>
                  </div>
                )}
                {extractedData?.financing?.payment_method && (
                  <div className="detail-item">
                    <span className="detail-label">Payment Method</span>
                    <span className="detail-value">
                      {extractedData.financing.payment_method === 'cash_with_new_mortgage' ? 'Mortgage' : extractedData.financing.payment_method}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contingencies & Dates */}
          {(extractedData?.transaction || extractedData?.contingencies) && (
            <div className="detail-section">
              <h3>📅 Contingencies & Important Dates</h3>
              <div className="detail-grid">
                {extractedData?.transaction?.closing_date && (
                  <div className="detail-item">
                    <span className="detail-label">Closing Date</span>
                    <span className="detail-value highlight">{formatDate(extractedData.transaction.closing_date)}</span>
                  </div>
                )}
                {extractedData?.transaction?.offer_expiration_date && (
                  <div className="detail-item">
                    <span className="detail-label">Offer Expiration</span>
                    <span className="detail-value">{formatDate(extractedData.transaction.offer_expiration_date)}</span>
                  </div>
                )}
                {extractedData?.contingencies?.appraisal !== null && extractedData?.contingencies?.appraisal !== undefined && (
                  <div className="detail-item">
                    <span className="detail-label">Appraisal Contingency</span>
                    <span className={`detail-value ${extractedData.contingencies.appraisal ? 'contingency-yes' : 'contingency-no'}`}>
                      {extractedData.contingencies.appraisal ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                )}
                {extractedData?.contingencies?.mortgage !== null && extractedData?.contingencies?.mortgage !== undefined && (
                  <div className="detail-item">
                    <span className="detail-label">Financing Contingency</span>
                    <span className={`detail-value ${extractedData.contingencies.mortgage ? 'contingency-yes' : 'contingency-no'}`}>
                      {extractedData.contingencies.mortgage ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                )}
                {extractedData?.contingencies?.inspection?.waived !== null && extractedData?.contingencies?.inspection?.waived !== undefined && (
                  <div className="detail-item">
                    <span className="detail-label">Inspection Waived</span>
                    <span className={`detail-value ${extractedData.contingencies.inspection!.waived ? 'contingency-yes' : 'contingency-no'}`}>
                      {extractedData.contingencies.inspection!.waived ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                )}
                {extractedData?.contingencies?.sale_of_buyers_property !== null && extractedData?.contingencies?.sale_of_buyers_property !== undefined && (
                  <div className="detail-item">
                    <span className="detail-label">Sale of Buyer Property</span>
                    <span className={`detail-value ${extractedData.contingencies.sale_of_buyers_property ? 'contingency-yes' : 'contingency-no'}`}>
                      {extractedData.contingencies.sale_of_buyers_property ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Special Clauses — Escalation & Appraisal Guarantee */}
          {(extractedData?.escalation_clause?.present || extractedData?.appraisal_guarantee?.present) && (
            <div className="detail-section">
              <h3>⚡ Special Clauses</h3>
              <div className="special-clauses-grid">

                {extractedData.escalation_clause?.present && (
                  <div className="special-clause-card escalation-card">
                    <div className="clause-card-header">
                      <span className="clause-card-icon">⚡</span>
                      <div>
                        <div className="clause-card-title">Escalation Clause</div>
                        <div className="clause-card-subtitle">Offer automatically beats competing offers</div>
                      </div>
                    </div>
                    <div className="clause-stats">
                      {extractedData.escalation_clause.increment_amount != null && (
                        <div className="clause-stat">
                          <span className="clause-stat-label">Increments By</span>
                          <span className="clause-stat-value">{formatCurrency(extractedData.escalation_clause.increment_amount)}</span>
                        </div>
                      )}
                      {extractedData.escalation_clause.max_cap != null && (
                        <div className="clause-stat">
                          <span className="clause-stat-label">Maximum Cap</span>
                          <span className="clause-stat-value accent">{formatCurrency(extractedData.escalation_clause.max_cap)}</span>
                        </div>
                      )}
                      {extractedData.escalation_clause.verification_method && (
                        <div className="clause-stat clause-stat-wide">
                          <span className="clause-stat-label">Verification</span>
                          <span className="clause-stat-value">{extractedData.escalation_clause.verification_method}</span>
                        </div>
                      )}
                      {extractedData.escalation_clause.expires_at && (
                        <div className="clause-stat">
                          <span className="clause-stat-label">Expires</span>
                          <span className="clause-stat-value">{formatDate(extractedData.escalation_clause.expires_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {extractedData.appraisal_guarantee?.present && (
                  <div className="special-clause-card appraisal-guarantee-card">
                    <div className="clause-card-header">
                      <span className="clause-card-icon">🛡️</span>
                      <div>
                        <div className="clause-card-title">Appraisal Guarantee</div>
                        <div className="clause-card-subtitle">Buyer commits to cover appraisal gap</div>
                      </div>
                    </div>
                    {extractedData.appraisal_guarantee.coverage_amount != null ? (
                      <div className="clause-stats">
                        <div className="clause-stat">
                          <span className="clause-stat-label">Coverage Amount</span>
                          <span className="clause-stat-value accent">Up to {formatCurrency(extractedData.appraisal_guarantee.coverage_amount)} above appraised value</span>
                        </div>
                      </div>
                    ) : (
                      <p className="clause-note">Buyer guarantees to cover any gap between purchase price and appraised value (amount not specified).</p>
                    )}
                  </div>
                )}

              </div>
            </div>
          )}

          {/* Buyer Agent (v2) */}
          {extractedData?.agents?.buyer_broker?.agent_name && (
            <div className="detail-section">
              <h3>🤝 Buyer Broker Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Agent Name</span>
                  <span className="detail-value highlight">{extractedData.agents.buyer_broker.agent_name}</span>
                </div>
                {extractedData.agents.buyer_broker.agent_id && (
                  <div className="detail-item">
                    <span className="detail-label">Agent License #</span>
                    <span className="detail-value">{extractedData.agents.buyer_broker.agent_id}</span>
                  </div>
                )}
                {extractedData.agents.buyer_broker.email && (
                  <div className="detail-item">
                    <span className="detail-label">Email</span>
                    <span className="detail-value">{extractedData.agents.buyer_broker.email}</span>
                  </div>
                )}
                {extractedData.agents.buyer_broker.phone && (
                  <div className="detail-item">
                    <span className="detail-label">Phone</span>
                    <span className="detail-value">{extractedData.agents.buyer_broker.phone}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pre-Approval Loan Details (moved below Buyer Broker Info) */}
          {(hasExtractedPreApproval || preApproval?.loan) && (
            <div className="detail-section">
              <h3>💰 Pre-Approval Loan Details</h3>
              <div className="detail-grid">
                {(extractedData?.pre_approval?.loan_type || preApproval?.loan?.loanType) && (
                  <div className="detail-item">
                    <span className="detail-label">Loan Type</span>
                    <span className="detail-value">{extractedData?.pre_approval?.loan_type || preApproval?.loan?.loanType}</span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Pre-Approved Price</span>
                  <span className="detail-value highlight">{formatCurrency(extractedData?.pre_approval?.max_purchase_price ?? preApproval?.loan?.purchasePrice ?? null)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Loan Amount</span>
                  <span className="detail-value highlight">{formatCurrency(extractedData?.pre_approval?.base_loan_amount ?? preApproval?.loan?.loanAmount ?? null)}</span>
                </div>
                {(preApproval?.loan?.downPayment != null) && (
                  <div className="detail-item">
                    <span className="detail-label">Down Payment</span>
                    <span className="detail-value">{formatCurrency(preApproval.loan.downPayment)}</span>
                  </div>
                )}
                {preApproval?.loan?.loanToValue && (
                  <div className="detail-item">
                    <span className="detail-label">LTV</span>
                    <span className="detail-value">{preApproval.loan.loanToValue}%</span>
                  </div>
                )}
                {preApproval?.loan?.estimatedInterestRate && (
                  <div className="detail-item">
                    <span className="detail-label">Interest Rate</span>
                    <span className="detail-value">{preApproval.loan.estimatedInterestRate}%</span>
                  </div>
                )}
                {preApproval?.loan?.term && (
                  <div className="detail-item">
                    <span className="detail-label">Term</span>
                    <span className="detail-value">{preApproval.loan.term}</span>
                  </div>
                )}
                {preApproval?.loan?.isRateLocked != null && (
                  <div className="detail-item">
                    <span className="detail-label">Rate Locked</span>
                    <span className="detail-value">{preApproval.loan.isRateLocked ? '✓ Yes' : '✗ No'}</span>
                  </div>
                )}
                {preApproval?.loan?.mortgageInsuranceRequired != null && (
                  <div className="detail-item">
                    <span className="detail-label">Mortgage Insurance</span>
                    <span className="detail-value">{preApproval.loan.mortgageInsuranceRequired ? 'Required' : 'Not Required'}</span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Expiration Date</span>
                  <span className="detail-value">{formatDate(extractedData?.pre_approval?.expiration_date || preApproval?.loan?.expirationDate)}</span>
                </div>
                {!extractedData?.pre_approval?.expiration_date && (
                  <div className="detail-item">
                    <span className="detail-label">Approval Date</span>
                    <span className="detail-value">{formatDate(preApproval?.loan?.approvalDate)}</span>
                  </div>
                )}
                {!extractedData && preApproval?.loan?.conditionalExpires && (
                  <div className="detail-item">
                    <span className="detail-label">Conditional Expires</span>
                    <span className="detail-value">{formatDate(preApproval.loan.conditionalExpires)}</span>
                  </div>
                )}
                {preApproval?.loan?.conditions && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Conditions</span>
                    <span className="detail-value conditions">{preApproval.loan.conditions}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* v1 Schema: Purchase Agreement Data (legacy) */}
      {!hasExtractedPurchaseAgreement && purchaseAgreement && (
        <>
          <div className="detail-section purchase-agreement-header">
            <h2>📋 Purchase Agreement Details</h2>
            {combinedOfferData?.metadata?.filesWithPurchaseAgreement && (
              <p className="source-file">Source: {combinedOfferData.metadata.filesWithPurchaseAgreement.join(', ')}</p>
            )}
          </div>

          {/* Property Details */}
          {purchaseAgreement.property && (
            <div className="detail-section">
              <h3>🏡 Property Information</h3>
              <div className="detail-grid">
                <div className="detail-item full-width">
                  <span className="detail-label">Address</span>
                  <span className="detail-value highlight">
                    {purchaseAgreement.property.address || 'N/A'}
                    {purchaseAgreement.property.city && `, ${purchaseAgreement.property.city}`}
                    {purchaseAgreement.property.state && `, ${purchaseAgreement.property.state}`}
                    {purchaseAgreement.property.zipCode && ` ${purchaseAgreement.property.zipCode}`}
                  </span>
                </div>
                {purchaseAgreement.property.county && (
                  <div className="detail-item">
                    <span className="detail-label">County</span>
                    <span className="detail-value">{purchaseAgreement.property.county}</span>
                  </div>
                )}
                {purchaseAgreement.property.taxId && (
                  <div className="detail-item">
                    <span className="detail-label">Tax ID / APN</span>
                    <span className="detail-value">{purchaseAgreement.property.taxId}</span>
                  </div>
                )}
                {purchaseAgreement.property.legalDescription && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Legal Description</span>
                    <span className="detail-value">{purchaseAgreement.property.legalDescription}</span>
                  </div>
                )}
                {purchaseAgreement.property.includedItems && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Included Items</span>
                    <span className="detail-value">{purchaseAgreement.property.includedItems}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Buyer Information */}
          {purchaseAgreement.buyer && (
            <div className="detail-section">
              <h3>🤝 Buyer Information</h3>
              <div className="detail-grid">
                {purchaseAgreement.buyer.name && (
                  <div className="detail-item">
                    <span className="detail-label">Buyer Name</span>
                    <span className="detail-value">{purchaseAgreement.buyer.name}</span>
                  </div>
                )}
                {purchaseAgreement.buyer.agentName && (
                  <div className="detail-item">
                    <span className="detail-label">Buyer's Agent</span>
                    <span className="detail-value highlight">{purchaseAgreement.buyer.agentName}</span>
                  </div>
                )}
                {purchaseAgreement.buyer.agentId && (
                  <div className="detail-item">
                    <span className="detail-label">Agent License #</span>
                    <span className="detail-value">{purchaseAgreement.buyer.agentId}</span>
                  </div>
                )}
                {purchaseAgreement.buyer.brokerName && (
                  <div className="detail-item">
                    <span className="detail-label">Broker Name</span>
                    <span className="detail-value">{purchaseAgreement.buyer.brokerName}</span>
                  </div>
                )}
                {purchaseAgreement.buyer.brokerId && (
                  <div className="detail-item">
                    <span className="detail-label">Broker License #</span>
                    <span className="detail-value">{purchaseAgreement.buyer.brokerId}</span>
                  </div>
                )}
                {purchaseAgreement.buyer.officeId && (
                  <div className="detail-item">
                    <span className="detail-label">Office ID</span>
                    <span className="detail-value">{purchaseAgreement.buyer.officeId}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Seller Information */}
          {purchaseAgreement.seller && (
            <div className="detail-section">
              <h3>🏠 Seller Information</h3>
              <div className="detail-grid">
                {purchaseAgreement.seller.name && (
                  <div className="detail-item">
                    <span className="detail-label">Seller Name</span>
                    <span className="detail-value">{purchaseAgreement.seller.name}</span>
                  </div>
                )}
                {purchaseAgreement.seller.agentName && (
                  <div className="detail-item">
                    <span className="detail-label">Listing Agent</span>
                    <span className="detail-value highlight">{purchaseAgreement.seller.agentName}</span>
                  </div>
                )}
                {purchaseAgreement.seller.agentId && (
                  <div className="detail-item">
                    <span className="detail-label">Agent License #</span>
                    <span className="detail-value">{purchaseAgreement.seller.agentId}</span>
                  </div>
                )}
                {purchaseAgreement.seller.brokerName && (
                  <div className="detail-item">
                    <span className="detail-label">Broker Name</span>
                    <span className="detail-value">{purchaseAgreement.seller.brokerName}</span>
                  </div>
                )}
                {purchaseAgreement.seller.brokerId && (
                  <div className="detail-item">
                    <span className="detail-label">Broker License #</span>
                    <span className="detail-value">{purchaseAgreement.seller.brokerId}</span>
                  </div>
                )}
                {purchaseAgreement.seller.officeId && (
                  <div className="detail-item">
                    <span className="detail-label">Office ID</span>
                    <span className="detail-value">{purchaseAgreement.seller.officeId}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Financial Terms */}
          {purchaseAgreement.financial && (
            <div className="detail-section">
              <h3>💵 Financial Terms</h3>
              <div className="detail-grid">
                {purchaseAgreement.financial.purchasePrice && (
                  <div className="detail-item">
                    <span className="detail-label">Purchase Price</span>
                    <span className="detail-value highlight">{formatCurrency(purchaseAgreement.financial.purchasePrice)}</span>
                  </div>
                )}
                {purchaseAgreement.financial.earnestMoneyTotal && (
                  <div className="detail-item">
                    <span className="detail-label">Earnest Money (Total)</span>
                    <span className="detail-value">{formatCurrency(purchaseAgreement.financial.earnestMoneyTotal)}</span>
                  </div>
                )}
                {purchaseAgreement.financial.earnestMoneyInitial && (
                  <div className="detail-item">
                    <span className="detail-label">Earnest Money (Initial)</span>
                    <span className="detail-value">{formatCurrency(purchaseAgreement.financial.earnestMoneyInitial)}</span>
                  </div>
                )}
                {purchaseAgreement.financial.earnestMoneyAdditional != null && purchaseAgreement.financial.earnestMoneyAdditional > 0 && (
                  <div className="detail-item">
                    <span className="detail-label">Earnest Money (Additional)</span>
                    <span className="detail-value">{formatCurrency(purchaseAgreement.financial.earnestMoneyAdditional)}</span>
                  </div>
                )}
                {purchaseAgreement.financial.downPayment && (
                  <div className="detail-item">
                    <span className="detail-label">Down Payment</span>
                    <span className="detail-value">{formatCurrency(purchaseAgreement.financial.downPayment)}</span>
                  </div>
                )}
                {purchaseAgreement.financial.loanAmount && (
                  <div className="detail-item">
                    <span className="detail-label">Loan Amount</span>
                    <span className="detail-value">{formatCurrency(purchaseAgreement.financial.loanAmount)}</span>
                  </div>
                )}
                {purchaseAgreement.financial.financingType && (
                  <div className="detail-item">
                    <span className="detail-label">Financing Type</span>
                    <span className="detail-value">{purchaseAgreement.financial.financingType}</span>
                  </div>
                )}
                {purchaseAgreement.financial.sellerConcessions && (
                  <div className="detail-item">
                    <span className="detail-label">Seller Concessions</span>
                    <span className="detail-value">{formatCurrency(purchaseAgreement.financial.sellerConcessions)}</span>
                  </div>
                )}
                {purchaseAgreement.financial.closingCosts && (
                  <div className="detail-item">
                    <span className="detail-label">Closing Costs</span>
                    <span className="detail-value">{formatCurrency(purchaseAgreement.financial.closingCosts)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contingencies & Dates */}
          {purchaseAgreement.contingencies && (
            <div className="detail-section">
              <h3>📅 Contingencies & Important Dates</h3>
              <div className="detail-grid">
                {purchaseAgreement.contingencies.closingDate && (
                  <div className="detail-item">
                    <span className="detail-label">Closing Date</span>
                    <span className="detail-value highlight">{formatDate(purchaseAgreement.contingencies.closingDate)}</span>
                  </div>
                )}
                {purchaseAgreement.contingencies.possessionDate && (
                  <div className="detail-item">
                    <span className="detail-label">Possession Date</span>
                    <span className="detail-value">{formatDate(purchaseAgreement.contingencies.possessionDate)}</span>
                  </div>
                )}
                {purchaseAgreement.contingencies.inspectionPeriod !== null && (
                  <div className="detail-item">
                    <span className="detail-label">Inspection Period</span>
                    <span className="detail-value">{purchaseAgreement.contingencies.inspectionPeriod} days</span>
                  </div>
                )}
                {purchaseAgreement.contingencies.offerExpirationDate && (
                  <div className="detail-item">
                    <span className="detail-label">Offer Expiration</span>
                    <span className="detail-value">{formatDate(purchaseAgreement.contingencies.offerExpirationDate)}</span>
                  </div>
                )}
                {purchaseAgreement.contingencies.financingContingency !== null && (
                  <div className="detail-item">
                    <span className="detail-label">Financing Contingency</span>
                    <span className={`detail-value ${purchaseAgreement.contingencies.financingContingency ? 'contingency-yes' : 'contingency-no'}`}>
                      {purchaseAgreement.contingencies.financingContingency ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                )}
                {purchaseAgreement.contingencies.appraisalContingency !== null && (
                  <div className="detail-item">
                    <span className="detail-label">Appraisal Contingency</span>
                    <span className={`detail-value ${purchaseAgreement.contingencies.appraisalContingency ? 'contingency-yes' : 'contingency-no'}`}>
                      {purchaseAgreement.contingencies.appraisalContingency ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                )}
                {purchaseAgreement.contingencies.homeInspectionContingency !== null && (
                  <div className="detail-item">
                    <span className="detail-label">Home Inspection Contingency</span>
                    <span className={`detail-value ${purchaseAgreement.contingencies.homeInspectionContingency ? 'contingency-yes' : 'contingency-no'}`}>
                      {purchaseAgreement.contingencies.homeInspectionContingency ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                )}
                {purchaseAgreement.contingencies.saleOfBuyerPropertyContingency !== null && (
                  <div className="detail-item">
                    <span className="detail-label">Sale of Buyer Property</span>
                    <span className={`detail-value ${purchaseAgreement.contingencies.saleOfBuyerPropertyContingency ? 'contingency-yes' : 'contingency-no'}`}>
                      {purchaseAgreement.contingencies.saleOfBuyerPropertyContingency ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Title & Escrow */}
          {purchaseAgreement.titleEscrow && (purchaseAgreement.titleEscrow.titleCompany || purchaseAgreement.titleEscrow.escrowCompany || purchaseAgreement.titleEscrow.titleInsuranceAmount) && (
            <div className="detail-section">
              <h3>📜 Title & Escrow Information</h3>
              <div className="detail-grid">
                {purchaseAgreement.titleEscrow.titleCompany && (
                  <div className="detail-item">
                    <span className="detail-label">Title Company</span>
                    <span className="detail-value">{purchaseAgreement.titleEscrow.titleCompany}</span>
                  </div>
                )}
                {purchaseAgreement.titleEscrow.escrowCompany && (
                  <div className="detail-item">
                    <span className="detail-label">Escrow Company</span>
                    <span className="detail-value">{purchaseAgreement.titleEscrow.escrowCompany}</span>
                  </div>
                )}
                {purchaseAgreement.titleEscrow.titleInsuranceAmount && (
                  <div className="detail-item">
                    <span className="detail-label">Title Insurance Amount</span>
                    <span className="detail-value">{formatCurrency(purchaseAgreement.titleEscrow.titleInsuranceAmount)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Recommendations Modal */}
      {showRecommendations && (
        <div className="modal-overlay" onClick={() => setShowRecommendations(false)}>
          <div className="recommendations-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>💡 All Recommendations to Improve Offer Score</h3>
              <button
                className="modal-close"
                onClick={() => setShowRecommendations(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="current-score-display">
                <span>Current Offer Score:</span>
                {liveScore !== null && (
                  <div className={`score-badge ${
                    liveScore >= 70 ? 'score-excellent' :
                    liveScore >= 50 ? 'score-fair' :
                    'score-poor'
                  }`}>
                    {Math.round(liveScore)}
                  </div>
                )}
              </div>

              <div className="recommendation-section">
                <h4>Recommendations to Strengthen This Offer</h4>
                <ul>
                  {offer.recommendations?.map((recommendation, idx) => (
                    <li key={idx}>{recommendation}</li>
                  ))}
                </ul>
              </div>

              <div className="modal-footer-note">
                <p><strong>Note:</strong> These recommendations are based on common factors that strengthen offers. Consult with your client before implementing any changes.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="disclaimer-footer">
        <div className="disclaimer-header">
          <span className="disclaimer-icon">ℹ️</span>
          <span className="disclaimer-title">Important Information</span>
        </div>
        <div className="disclaimer-text">
          The information displayed is extracted automatically from submitted documents and may not include all details from the purchase agreement.
          Please verify all critical information from the actual documents. The PTC (Probability to Close) score is a suggestion based on specific criteria and should be used as a guideline only.
        </div>
      </div>

      {/* Edit Offer Modal */}
      {showEditModal && offer && (
        <OfferEditModal
          offer={offer}
          lenderExperienceYears={lenderExperience}
          onClose={() => setShowEditModal(false)}
          onSaved={(updatedOffer) => {
            setOffer(updatedOffer);
            setShowEditModal(false);
          }}
        />
      )}
    </div>
  );
};

export default OfferDetail;
